import React from 'react';

import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { useStatsigGate } from 'src/contexts/feature-provider';

interface ChildProps {
  sentryId?: string;
  // can include whatever the child components props are
  [key: string]: any;
}

interface DisplaySentryIdToUserCheckProps {
  children: React.ReactElement<ChildProps>;
}

/**
 * The component checks if the `bbc-sentry-ids-in-error-ui-m1`
 * feature is enabled. The `sentryId` prop is what controls whether the
 * Sentry ID is displayed to the user or not. If enabled, the `sentryId`
 * prop is passed to the child component. If disabled, the `sentryId`
 * prop is set to undefined (and therefore won't get rendered).
 *
 * Once `bbc-sentry-ids-in-error-ui-m1` is fully rolled out, this container
 * can just be removed from components and props don't need to be changed
 *
 * @param {React.ReactElement} children - The child component to be wrapped.
 * @returns {React.ReactElement} The same child component with the `sentryId` prop
 * conditionally included or excluded.
 *

 */
export function DisplaySentryIdToUserCheck({
  children,
}: DisplaySentryIdToUserCheckProps): React.ReactElement {
  // Check if the Sentry ID feature is enabled using the Statsig feature gate
  const isSentryIdEnabled = useStatsigGate(
    StatsigFeatureKeys.bbcSentryIdsInErrorUiM1
  );

  const origChildrenProps = children.props || ({} as ChildProps);

  // If the feature is not enabled and the child component has a `sentryId` prop,
  // remove the `sentryId` prop from the child component
  if (!isSentryIdEnabled && origChildrenProps.sentryId) {
    return React.cloneElement(children, {
      ...origChildrenProps,
      sentryId: undefined,
    });
  } else {
    return children;
  }
}
